import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Lock,
  Mail,
  MessageCircle,
  Link2,
  Unlink,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Send,
  Settings as SettingsIcon,
} from 'lucide-react';

// Social login icons (inline SVG for custom branding)
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FFDC80"/>
        <stop offset="25%" stopColor="#F77737"/>
        <stop offset="50%" stopColor="#E1306C"/>
        <stop offset="75%" stopColor="#C13584"/>
        <stop offset="100%" stopColor="#833AB4"/>
      </linearGradient>
    </defs>
    <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-[#25D366]">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const SettingsPage = () => {
  const { toast } = useToast();
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Connected accounts state
  const [connectedAccounts, setConnectedAccounts] = useState({
    google: { connected: true, email: 'admin@techcorp.com' },
    instagram: { connected: false, username: '' },
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    emailShortlisted: true,
    emailInterview: true,
    emailTaskAssigned: true,
    whatsappShortlisted: false,
    whatsappInterview: true,
    whatsappReminder: true,
  });

  // WhatsApp settings
  const [whatsappNumber, setWhatsappNumber] = useState('+1 234 567 8900');
  const [whatsappVerified, setWhatsappVerified] = useState(true);

  const handlePasswordChange = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all password fields.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'New password and confirm password must match.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Weak password',
        description: 'Password must be at least 8 characters long.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Password updated',
      description: 'Your password has been changed successfully.',
    });
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleConnectAccount = (provider: 'google' | 'instagram') => {
    if (connectedAccounts[provider].connected) {
      setConnectedAccounts(prev => ({
        ...prev,
        [provider]: { connected: false, email: '', username: '' },
      }));
      toast({
        title: 'Account disconnected',
        description: `Your ${provider === 'google' ? 'Google' : 'Instagram'} account has been disconnected.`,
      });
    } else {
      // Simulate OAuth connection
      setConnectedAccounts(prev => ({
        ...prev,
        [provider]: {
          connected: true,
          email: provider === 'google' ? 'admin@techcorp.com' : '',
          username: provider === 'instagram' ? '@techcorp_hiring' : '',
        },
      }));
      toast({
        title: 'Account connected',
        description: `Your ${provider === 'google' ? 'Google' : 'Instagram'} account has been connected successfully.`,
      });
    }
  };

  const handleSaveNotifications = () => {
    toast({
      title: 'Settings saved',
      description: 'Your notification preferences have been updated.',
    });
  };

  const handleTestEmail = () => {
    toast({
      title: 'Test email sent',
      description: 'A test email has been sent to your connected email address.',
    });
  };

  const handleTestWhatsApp = () => {
    toast({
      title: 'Test WhatsApp message sent',
      description: 'A test message has been sent to your WhatsApp number.',
    });
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Manage your account, security, and notification preferences</p>
      </div>

      <Tabs defaultValue="security" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="security" className="gap-2">
            <Lock className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="connections" className="gap-2">
            <Link2 className="h-4 w-4" />
            Connected Accounts
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Mail className="h-4 w-4" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Password & Security
              </CardTitle>
              <CardDescription>
                Update your password and manage account security settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                Password must be at least 8 characters with a mix of letters and numbers
              </div>

              <Button onClick={handlePasswordChange} className="gap-2">
                <Lock className="h-4 w-4" />
                Update Password
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connected Accounts Tab */}
        <TabsContent value="connections">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                Connected Accounts
              </CardTitle>
              <CardDescription>
                Manage your social login connections for quick access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Google */}
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <GoogleIcon />
                  </div>
                  <div>
                    <h3 className="font-semibold">Google</h3>
                    {connectedAccounts.google.connected ? (
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">{connectedAccounts.google.email}</p>
                        <Badge variant="secondary" className="gap-1 bg-success/10 text-success">
                          <Check className="h-3 w-3" />
                          Connected
                        </Badge>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not connected</p>
                    )}
                  </div>
                </div>
                <Button
                  variant={connectedAccounts.google.connected ? 'outline' : 'default'}
                  onClick={() => handleConnectAccount('google')}
                  className="gap-2"
                >
                  {connectedAccounts.google.connected ? (
                    <>
                      <Unlink className="h-4 w-4" />
                      Disconnect
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4" />
                      Connect
                    </>
                  )}
                </Button>
              </div>

              {/* Instagram */}
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <InstagramIcon />
                  </div>
                  <div>
                    <h3 className="font-semibold">Instagram</h3>
                    {connectedAccounts.instagram.connected ? (
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">{connectedAccounts.instagram.username}</p>
                        <Badge variant="secondary" className="gap-1 bg-success/10 text-success">
                          <Check className="h-3 w-3" />
                          Connected
                        </Badge>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not connected</p>
                    )}
                  </div>
                </div>
                <Button
                  variant={connectedAccounts.instagram.connected ? 'outline' : 'default'}
                  onClick={() => handleConnectAccount('instagram')}
                  className="gap-2"
                >
                  {connectedAccounts.instagram.connected ? (
                    <>
                      <Unlink className="h-4 w-4" />
                      Disconnect
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4" />
                      Connect
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="grid gap-6">
            {/* Email Notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Email Notifications
                </CardTitle>
                <CardDescription>
                  Configure when to send email notifications to candidates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="emailShortlisted">Shortlisted Notification</Label>
                    <p className="text-sm text-muted-foreground">
                      Send email when a candidate is shortlisted
                    </p>
                  </div>
                  <Switch
                    id="emailShortlisted"
                    checked={notifications.emailShortlisted}
                    onCheckedChange={(checked) =>
                      setNotifications(prev => ({ ...prev, emailShortlisted: checked }))
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="emailInterview">Interview Scheduled</Label>
                    <p className="text-sm text-muted-foreground">
                      Send email when an interview is scheduled
                    </p>
                  </div>
                  <Switch
                    id="emailInterview"
                    checked={notifications.emailInterview}
                    onCheckedChange={(checked) =>
                      setNotifications(prev => ({ ...prev, emailInterview: checked }))
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="emailTaskAssigned">Task Assigned</Label>
                    <p className="text-sm text-muted-foreground">
                      Send email when a new task is assigned
                    </p>
                  </div>
                  <Switch
                    id="emailTaskAssigned"
                    checked={notifications.emailTaskAssigned}
                    onCheckedChange={(checked) =>
                      setNotifications(prev => ({ ...prev, emailTaskAssigned: checked }))
                    }
                  />
                </div>

                <div className="pt-4">
                  <Button variant="outline" onClick={handleTestEmail} className="gap-2">
                    <Send className="h-4 w-4" />
                    Send Test Email
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp Notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <WhatsAppIcon />
                  WhatsApp Notifications
                </CardTitle>
                <CardDescription>
                  Send WhatsApp messages to candidates for important updates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* WhatsApp Business Number */}
                <div className="rounded-lg border border-border p-4 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">WhatsApp Business Number</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          value={whatsappNumber}
                          onChange={(e) => setWhatsappNumber(e.target.value)}
                          className="w-48"
                        />
                        {whatsappVerified && (
                          <Badge variant="secondary" className="gap-1 bg-success/10 text-success">
                            <Check className="h-3 w-3" />
                            Verified
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Verify Number
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="whatsappShortlisted">Shortlisted Notification</Label>
                    <p className="text-sm text-muted-foreground">
                      Send WhatsApp when a candidate is shortlisted
                    </p>
                  </div>
                  <Switch
                    id="whatsappShortlisted"
                    checked={notifications.whatsappShortlisted}
                    onCheckedChange={(checked) =>
                      setNotifications(prev => ({ ...prev, whatsappShortlisted: checked }))
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="whatsappInterview">Interview Updates</Label>
                    <p className="text-sm text-muted-foreground">
                      Send WhatsApp when an interview is scheduled or rescheduled
                    </p>
                  </div>
                  <Switch
                    id="whatsappInterview"
                    checked={notifications.whatsappInterview}
                    onCheckedChange={(checked) =>
                      setNotifications(prev => ({ ...prev, whatsappInterview: checked }))
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="whatsappReminder">Interview Reminders</Label>
                    <p className="text-sm text-muted-foreground">
                      Send reminder 24 hours before scheduled interviews
                    </p>
                  </div>
                  <Switch
                    id="whatsappReminder"
                    checked={notifications.whatsappReminder}
                    onCheckedChange={(checked) =>
                      setNotifications(prev => ({ ...prev, whatsappReminder: checked }))
                    }
                  />
                </div>

                <div className="pt-4">
                  <Button variant="outline" onClick={handleTestWhatsApp} className="gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Send Test WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveNotifications} className="gap-2">
                <SettingsIcon className="h-4 w-4" />
                Save Notification Settings
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default SettingsPage;
